import dayjs from 'dayjs';
import React from 'react';
import { useAppSelector } from '../app/hooks/store-hooks';
import { IntegrationStatus } from '../common/enums';
import { FlightContextMenuDto } from '../flights/interop/flightDto';
import { RouteRequestType } from '../services/actor-messages';
import { requestFpmGoToFlight, requestFpmRoutes } from '../services/integration/fpm-request-tracker';
import useUserRights from './use-user-rights';

export const FPMGoToFlight = (flightId: number, acid: string | null) => {
    return requestFpmGoToFlight(flightId, acid);
};

const requestRoutes = (flight: FlightContextMenuDto, type: RouteRequestType) => {
    return requestFpmRoutes({
        acid: flight.acid,
        origin: flight.origin,
        destination: flight.destination,
        edt: flight.oetd ? dayjs(flight.oetd).toDate() : null,
        type
    });
};

export const FPMRequestRoute = (flight: FlightContextMenuDto) => requestRoutes(flight, RouteRequestType.Routes);

export const FPMRequestRouteVariants = (flight: FlightContextMenuDto) => requestRoutes(flight, RouteRequestType.Variants);

export type IntegrationAction = keyof ReturnType<typeof useUserRights>;

export interface IntegrationInfo {
    typeText: string;
    status: IntegrationStatus;
    enabled: boolean;
    isConnected: boolean;
    isFPM: boolean;
    isDesktop: boolean;
    isActionEnabled: (action: IntegrationAction) => boolean;
    getDisabledReason: (action: IntegrationAction) => string | null;
}

const integrationGrantedRights: IntegrationAction[] = [
    'canGoToFlightInFE',
    'canGoToFlightInFPM',
    'canSendDrawObjToFPM',
    'canSendRouteToFE',
    'canRequestRouteFromFPM',
    'canFlightReleaseToFE',
    'canVerifyRouteInFPM',
    'canCalcRouteInFPM',
    'canDeleteDrawObjFromFPM',
    'canSendUserDrawObjToFPM',
    'canDeleteUserDrawObjFromFPM'
];

const useIntegration = () => {
    const userRights = useAppSelector((x) => x.user.rights);
    const integrationState = useAppSelector((state) => state.global.integrationState);
    const enabled = userRights.FPMIntegration || userRights.DesktopIntegration;

    const integrationType = React.useMemo(() => {
        if (userRights.FPMIntegration) {
            return 'FPM';
        }
        if (userRights.DesktopIntegration) {
            return integrationState.desktopAppName;
        }
        return 'None';
    }, [userRights.FPMIntegration, userRights.DesktopIntegration, integrationState]);

    const getDisabledReason = React.useCallback(
        (action: IntegrationAction) => {
            if (!enabled) {
                return 'Integration is not enabled for your account';
            }
            switch (integrationState.status) {
                case IntegrationStatus.Ok:
                    break;
                case IntegrationStatus.NoRights:
                    return `${integrationType} is not connected for this session`;
                case IntegrationStatus.Duplicate:
                    return 'Your integration ID is already in use by another session';
                default:
                    return `No reply from ${integrationType}`;
            }
            if (!userRights[action]) {
                // All rights missing usually means the integration could not be reached when logging in
                return integrationGrantedRights.some((x) => userRights[x])
                    ? `Not permitted by ${integrationType}`
                    : `${integrationType} is unavailable or did not grant any permissions`;
            }
            return null;
        },
        [enabled, userRights, integrationState.status, integrationType]
    );

    const isActionEnabled = React.useCallback((action: IntegrationAction) => getDisabledReason(action) === null, [getDisabledReason]);

    return {
        typeText: integrationType,
        status: integrationState.status,
        enabled,
        isConnected: integrationState.status === IntegrationStatus.Ok,
        isFPM: userRights.FPMIntegration,
        isDesktop: userRights.DesktopIntegration && !userRights.FPMIntegration,
        isActionEnabled,
        getDisabledReason
    } as IntegrationInfo;
};

export default useIntegration;
